import React, { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';
import { telemetry } from '../../../../telemetry';
import { uniqueId } from '../../../../lib/uniqueId';
import { OPTION_ALL } from '../../../common/filters/single-select-filter/utils';
import { SingleSelectFilterPopoverOption } from '../../../common/filters/single-select-filter/SingleSelectFilterPopover';

export interface UseSearchStateConfig<TResults> {
  searchFn: (term: string, searchType: string) => Promise<TResults>;
  defaultSearchType: string;
  searchTypeOptions: SingleSelectFilterPopoverOption[];
  buildFilterOptions: (results: TResults | undefined) => SingleSelectFilterPopoverOption[];
  filterResults: (results: TResults, filterKey: string) => TResults | undefined;
  onClose?: () => void;
  initialSearchTerm?: string;
  initialResults?: TResults;
}

export interface UseSearchStateReturn<TResults> {
  searchTxt: string;
  tempSearchTxt: string;
  setTempSearchTxt: React.Dispatch<React.SetStateAction<string>>;
  isLoading: boolean;
  results: TResults | undefined;
  hasSearched: boolean;
  isSearchListOpen: boolean;
  setIsSearchListOpen: React.Dispatch<React.SetStateAction<boolean>>;
  performSearch: (term: string, searchType?: string) => Promise<TResults | undefined>;
  onSearchChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onClearButtonClick: () => void;
  selectedSearchType: string;
  onSelectSearchType: (value: string) => Promise<TResults | undefined>;
  searchTypeOptions: SingleSelectFilterPopoverOption[];
  filterOptions: SingleSelectFilterPopoverOption[];
  filterKey: string | undefined;
  onSelectedFilterItem: (key?: string) => void;
  reset: () => void;
}

export const useSearchState = <TResults>({
  searchFn,
  defaultSearchType,
  searchTypeOptions,
  buildFilterOptions,
  filterResults,
  onClose,
  initialSearchTerm = '',
  initialResults,
}: UseSearchStateConfig<TResults>): UseSearchStateReturn<TResults> => {
  const [searchTxt, setSearchTxt] = useState(initialSearchTerm);
  const [tempSearchTxt, setTempSearchTxt] = useState(initialSearchTerm);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<TResults | undefined>(initialResults);
  const [selectedSearchType, setSelectedSearchType] = useState(defaultSearchType);
  const [filterKey, setFilterKey] = useState<string | undefined>(undefined);
  const [isSearchListOpen, setIsSearchListOpen] = useState(false);
  const requestIdRef = useRef('');

  const performSearch = useCallback(
    async (term: string, searchType?: string): Promise<TResults | undefined> => {
      const trimmed = term.trim();
      if (!trimmed) {
        setSearchTxt('');
        setResults(undefined);
        return undefined;
      }

      const reqId = uniqueId();
      requestIdRef.current = reqId;
      setIsLoading(true);
      setSearchTxt(trimmed);

      try {
        const data = await searchFn(trimmed, searchType ?? selectedSearchType);
        if (requestIdRef.current === reqId) {
          setResults(data);
          return data;
        }
        return undefined;
      } catch (e) {
        if (requestIdRef.current === reqId) {
          telemetry.captureError(e);
          setResults(undefined);
        }
        return undefined;
      } finally {
        if (requestIdRef.current === reqId) {
          setIsLoading(false);
        }
      }
    },
    [searchFn, selectedSearchType],
  );

  const onSearchChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTempSearchTxt(e.target.value);
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        onClose?.();
        return;
      }
      if (e.key === 'Enter') {
        performSearch(tempSearchTxt);
      }
    },
    [tempSearchTxt, performSearch, onClose],
  );

  const onClearButtonClick = useCallback(() => {
    requestIdRef.current = '';
    setSearchTxt('');
    setTempSearchTxt('');
    setResults(undefined);
    setIsLoading(false);
    setFilterKey(undefined);
  }, []);

  const onSelectSearchType = useCallback(
    async (value: string): Promise<TResults | undefined> => {
      setSelectedSearchType(value);
      if (searchTxt) {
        return await performSearch(searchTxt, value);
      }
      return undefined;
    },
    [searchTxt, performSearch],
  );

  const filterOptions = useMemo(() => buildFilterOptions(results), [results, buildFilterOptions]);

  const filteredResults = useMemo(() => {
    if (!results) return undefined;
    if (!filterKey || filterKey === OPTION_ALL.value) return results;
    return filterResults(results, filterKey);
  }, [results, filterKey, filterResults]);

  const onSelectedFilterItem = useCallback((key?: string) => {
    setFilterKey(key);
  }, []);

  const reset = useCallback(() => {
    requestIdRef.current = '';
    setSearchTxt('');
    setTempSearchTxt('');
    setResults(undefined);
    setIsLoading(false);
    setFilterKey(undefined);
    setSelectedSearchType(defaultSearchType);
    setIsSearchListOpen(false);
  }, [defaultSearchType]);

  return {
    searchTxt,
    tempSearchTxt,
    setTempSearchTxt,
    isLoading,
    results: filteredResults,
    hasSearched: results !== undefined,
    isSearchListOpen,
    setIsSearchListOpen,
    performSearch,
    onSearchChange,
    onKeyDown,
    onClearButtonClick,
    selectedSearchType,
    onSelectSearchType,
    searchTypeOptions,
    filterOptions,
    filterKey,
    onSelectedFilterItem,
    reset,
  };
};
